import { useRef, useState } from "react";
import { QRCodeCanvas } from "qrcode.react";
import { X, Download, Copy, Check } from "lucide-react";
import { toast } from "sonner";

/**
 * Modal that renders a QR code for a public event URL and lets admins download it as a
 * high-resolution PNG (1024×1024) — big enough to print onto a poster without pixelation.
 * The canvas is rendered off-screen at 1024px so the downloaded PNG stays sharp.
 */
export default function EventQrModal({ event, onClose }) {
  const printCanvasRef = useRef(null);
  const [copied, setCopied] = useState(false);
  const url = `${window.location.origin}/events/${event.slug}`;

  const download = () => {
    const canvas = printCanvasRef.current?.querySelector("canvas");
    if (!canvas) return;
    const link = document.createElement("a");
    link.download = `carpe-diem-${event.slug}-qr.png`;
    link.href = canvas.toDataURL("image/png");
    link.click();
    toast.success("QR downloaded — ready for print");
  };

  const copyUrl = async () => {
    try {
      await navigator.clipboard.writeText(url);
      setCopied(true);
      setTimeout(() => setCopied(false), 1600);
    } catch { toast.error("Could not copy"); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-6" onClick={onClose}>
      <div className="bg-cream max-w-lg w-full border border-soft" onClick={(e) => e.stopPropagation()} data-testid="event-qr-modal">
        <div className="flex items-center justify-between p-5 border-b border-soft">
          <div>
            <div className="overline">QR code</div>
            <div className="font-serif text-xl mt-1">{event.title}</div>
          </div>
          <button onClick={onClose} data-testid="event-qr-close"><X size={18}/></button>
        </div>

        <div className="p-6 flex flex-col items-center">
          {/* Visible QR — 240×240 with the brand terra colour */}
          <div className="p-4 bg-white border border-soft" data-testid="event-qr-visible">
            <QRCodeCanvas
              value={url}
              size={240}
              level="M"
              fgColor="#1C1C19"
              bgColor="#FFFFFF"
              includeMargin
            />
          </div>

          {/* Off-screen high-res canvas used for the download only */}
          <div ref={printCanvasRef} style={{ position: "absolute", left: -99999, top: -99999 }} aria-hidden="true">
            <QRCodeCanvas
              value={url}
              size={1024}
              level="H"
              fgColor="#1C1C19"
              bgColor="#FFFFFF"
              includeMargin
            />
          </div>

          <div className="mt-5 w-full bg-cream-2 border border-soft p-3 text-xs break-all flex items-center gap-2" data-testid="event-qr-url">
            <span className="flex-1">{url}</span>
            <button onClick={copyUrl} className="inline-flex items-center gap-1 text-muted2 hover:text-charcoal" data-testid="event-qr-copy">
              {copied ? <><Check size={12} style={{color:"#4A5D4E"}}/> Copied</> : <><Copy size={12}/> Copy</>}
            </button>
          </div>

          <p className="mt-4 text-xs text-muted2 text-center max-w-sm leading-relaxed">
            Scanning this code opens the event page where visitors can book instantly. Downloads at 1024&nbsp;×&nbsp;1024&nbsp;px — sharp enough for posters and flyers.
          </p>

          <button onClick={download} className="btn-primary mt-5 w-full justify-center" data-testid="event-qr-download">
            <Download size={14}/> Download PNG (1024×1024)
          </button>
        </div>
      </div>
    </div>
  );
}
